import { useState, useMemo } from 'react';
import { Search, X, Image as ImageIcon, Plus, Eye, Filter } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { assetsApi } from '@/lib/api';

interface AssetLibraryProps {
  facilityId: string;
  onAddToTemplate?: (assetId: number) => void;
  assetsInTemplate?: number[]; // IDs of assets already in the current template
  showAddButton?: boolean;
  allowUpload?: boolean;
}

export default function AssetLibrary({
  facilityId,
  onAddToTemplate,
  assetsInTemplate = [],
  showAddButton = true,
  allowUpload = false,
}: AssetLibraryProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMode, setFilterMode] = useState<'all' | 'inTemplate' | 'notInTemplate' | 'orphaned'>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedAsset, setSelectedAsset] = useState<number | null>(null);

  // Fetch assets
  const { data: assetsData, isLoading } = useQuery({
    queryKey: ['asset-library', facilityId],
    queryFn: () => assetsApi.list({ facilityId, limit: 100 }),
    enabled: !!facilityId,
  });

  const assets = assetsData?.assets || [];

  // Filter and search assets
  const filteredAssets = useMemo(() => {
    let filtered = assets;

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (asset) =>
          asset.originalName.toLowerCase().includes(term) ||
          asset.filename.toLowerCase().includes(term)
      );
    }

    // Apply template filter
    if (filterMode === 'inTemplate') {
      filtered = filtered.filter((asset) => assetsInTemplate.includes(asset.id));
    } else if (filterMode === 'notInTemplate') {
      filtered = filtered.filter((asset) => !assetsInTemplate.includes(asset.id));
    } else if (filterMode === 'orphaned') {
      // TODO: This would require checking if asset is in ANY template via API
      // For now, just show all assets not in current template
      filtered = filtered.filter((asset) => !assetsInTemplate.includes(asset.id));
    }

    return filtered;
  }, [assets, searchTerm, filterMode, assetsInTemplate]);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const handleAddToTemplate = (assetId: number) => {
    if (onAddToTemplate) {
      onAddToTemplate(assetId);
    }
  };

  const isInTemplate = (assetId: number) => assetsInTemplate.includes(assetId);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <ImageIcon className="h-5 w-5" />
            Asset Library
          </h3>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => setViewMode('grid')}
              className={
                viewMode === 'grid'
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
              }
            >
              Grid
            </Button>
            <Button
              size="sm"
              onClick={() => setViewMode('list')}
              className={
                viewMode === 'list'
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
              }
            >
              List
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search assets by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1">
            <Filter className="h-3 w-3" />
            Show:
          </span>
          <Button
            size="sm"
            onClick={() => setFilterMode('all')}
            className={`text-xs h-7 ${
              filterMode === 'all'
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
            }`}
          >
            All ({assets.length})
          </Button>
          {assetsInTemplate.length > 0 && (
            <>
              <Button
                size="sm"
                onClick={() => setFilterMode('inTemplate')}
                className={`text-xs h-7 ${
                  filterMode === 'inTemplate'
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                In Template ({assetsInTemplate.length})
              </Button>
              <Button
                size="sm"
                onClick={() => setFilterMode('notInTemplate')}
                className={`text-xs h-7 ${
                  filterMode === 'notInTemplate'
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                Not in Template ({assets.length - assetsInTemplate.length})
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Asset List */}
      <div className="flex-1 overflow-y-auto p-4 bg-gray-50 dark:bg-gray-900">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Loading assets...</p>
            </div>
          </div>
        ) : filteredAssets.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <ImageIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400 mb-2">
                {searchTerm
                  ? 'No assets match your search'
                  : filterMode === 'notInTemplate'
                  ? 'All assets are in the template'
                  : 'No assets found'}
              </p>
              {searchTerm && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSearchTerm('')}
                  className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  Clear search
                </Button>
              )}
            </div>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredAssets.map((asset) => (
              <div
                key={asset.id}
                className={`group relative rounded-lg border ${
                  isInTemplate(asset.id)
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                } overflow-hidden hover:shadow-lg transition-all cursor-pointer`}
                onClick={() => setSelectedAsset(selectedAsset === asset.id ? null : asset.id)}
              >
                {/* Thumbnail */}
                <div className="aspect-video bg-gray-100 dark:bg-gray-700 relative overflow-hidden">
                  <img
                    src={asset.thumbnailUrl || asset.s3Url}
                    alt={asset.originalName}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                  {isInTemplate(asset.id) && (
                    <div className="absolute top-2 right-2">
                      <Badge className="bg-blue-600 text-white">In Template</Badge>
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="p-3">
                  <p className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate" title={asset.originalName}>
                    {asset.originalName}
                  </p>
                  <div className="flex items-center justify-between mt-2 text-xs text-gray-600 dark:text-gray-400">
                    <span>{asset.width}×{asset.height}</span>
                    <span>{formatFileSize(asset.fileSize)}</span>
                  </div>

                  {/* Actions */}
                  {selectedAsset === asset.id && (
                    <div className="mt-3 flex items-center gap-2">
                      {showAddButton && !isInTemplate(asset.id) && onAddToTemplate && (
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddToTemplate(asset.id);
                          }}
                          className="flex-1 text-xs bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Add
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(asset.s3Url, '_blank');
                        }}
                        className="flex-1 text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        View
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {filteredAssets.map((asset) => (
              <div
                key={asset.id}
                className={`flex items-center gap-4 p-3 rounded-lg border ${
                  isInTemplate(asset.id)
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                } hover:shadow-md transition-all`}
              >
                {/* Thumbnail */}
                <div className="w-16 h-16 flex-shrink-0 bg-gray-100 dark:bg-gray-700 rounded overflow-hidden">
                  <img
                    src={asset.thumbnailUrl || asset.s3Url}
                    alt={asset.originalName}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
                    {asset.originalName}
                  </p>
                  <div className="flex items-center gap-4 mt-1 text-xs text-gray-600 dark:text-gray-400">
                    <span>{asset.width}×{asset.height}</span>
                    <span>{formatFileSize(asset.fileSize)}</span>
                    <span>{asset.mimeType}</span>
                  </div>
                </div>

                {/* Status Badge */}
                {isInTemplate(asset.id) && (
                  <Badge className="bg-blue-600 text-white">In Template</Badge>
                )}

                {/* Actions */}
                <div className="flex items-center gap-2">
                  {showAddButton && !isInTemplate(asset.id) && onAddToTemplate && (
                    <Button
                      size="sm"
                      onClick={() => handleAddToTemplate(asset.id)}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(asset.s3Url, '_blank')}
                    className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer Stats */}
      <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
          <span>
            Showing {filteredAssets.length} of {assets.length} assets
          </span>
          {allowUpload && (
            <Button
              size="sm"
              variant="outline"
              className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              <Plus className="h-3 w-3 mr-1" />
              Upload New
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
